import { useMemo, useRef } from "react";
import { Children, isValidElement } from "react";
import HorizontalScrollable from "domains/commons/components/HorizontalScrollable";
import { TOP_BAR_HEIGHT } from "domains/navigation/constants/TopBar";
import Button from "domains/ui/components/Button";
import { CollapseTransition } from "domains/ui/components/Collapse";
import Icon from "domains/ui/components/Icon";

import {
  Box,
  FlexProps,
  HStack,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import FmHeaderColumn from "./FmHeaderColumn";

export type FmHeaderProps = {
  title?: React.ReactNode;
  filterCount?: number;
  filterContent?: React.ReactNode;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  styleProps?: FlexProps;
  isLoading?: boolean;

  columnsCount?: number;
  minColumnsCount?: number;
  onColumnsCountChange?: (columnsCount: number) => void;

  isWithFilterButton?: boolean;
};

export default function FmHeader({
  title,
  filterCount,
  filterContent,
  leftContent,
  rightContent,
  styleProps,
  isLoading,

  columnsCount,
  minColumnsCount,
  onColumnsCountChange,

  isWithFilterButton = true,
}: FmHeaderProps) {
  const filtersRef = useRef<HTMLDivElement>(null);
  const { isOpen: isFilterVisible, onToggle: onFilterCollapseToggle } =
    useDisclosure();

  const hasFilterContent = useMemo(() => {
    if (!filterContent) return false;

    // If filterContent is a React element with children, check its children
    if (isValidElement(filterContent) && filterContent.props.children) {
      const children = Children.toArray(filterContent.props.children);
      return children.filter(Boolean).length > 0;
    }

    // Otherwise, treat filterContent itself as the content
    const children = Children.toArray(filterContent);
    return children.filter(Boolean).length > 0;
  }, [filterContent]);

  return (
    <Box
      pos="sticky"
      zIndex="sticky"
      top={`${TOP_BAR_HEIGHT}px`}
      my={2}
      bg="background.500"
      {...styleProps}
    >
      <HStack justify="space-between" spacing={2}>
        <HStack flex={1} spacing={2}>
          {!!title && <Text size="body.bold.lg">{title}</Text>}

          {hasFilterContent &&
            (isWithFilterButton ? (
              <Button
                variant="filter"
                size="sm"
                leftIcon={<Icon id="Ui/Filter" h="14px" />}
                onClick={onFilterCollapseToggle}
                {...(filterCount && {
                  borderColor: "textPrimary",
                  color: "textPrimary",
                })}
              >
                <HStack spacing={1}>
                  <span>Filters</span>
                  {!!filterCount && <span>{`(${filterCount})`}</span>}
                </HStack>
              </Button>
            ) : (
              <>{filterContent}</>
            ))}

          {leftContent}
        </HStack>

        <HStack spacing={1}>
          {isLoading && <Spinner size="sm" />}

          {onColumnsCountChange && (
            <FmHeaderColumn
              value={columnsCount ?? 3}
              min={minColumnsCount}
              onChange={onColumnsCountChange}
            />
          )}

          {rightContent}
        </HStack>
      </HStack>

      {hasFilterContent && isWithFilterButton && (
        <CollapseTransition animateOpacity={false} in={isFilterVisible}>
          <HorizontalScrollable
            childrenRef={filtersRef}
            getShouldAvoidScroll={(e) => {
              return !!(
                e.target instanceof HTMLElement &&
                e.target.closest(".chakra-menu__menu-list")
              );
            }}
            gradientColor="#121212"
          >
            <HStack
              ref={filtersRef}
              sx={{
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
              overflow="auto"
              mt={2}
              spacing={2}
            >
              {filterContent}
            </HStack>
          </HorizontalScrollable>
        </CollapseTransition>
      )}
    </Box>
  );
}
